import { AppBar, Autocomplete, Chip, FormControl, Grid, InputLabel, SelectChangeEvent, TextField, Toolbar } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { ColoredIconButton } from '../common/ColoredIconButton'
import AddIcon from '@mui/icons-material/Add'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { useSelector } from '../../redux/hooks/useSelector'
import { useDispatch } from 'react-redux'
import { openDialog } from '../../redux/actions/dialogActions'
import { CLIENT_INFORMATION_DIALOG_ID } from '../../constants/dialogConstants'
import {
  filterClientAmountDown,
  filterClientAmountUp,
  filterClientName,
  filterRole,
  sortClients,
} from '../../redux/actions/filterActions'
import { ClientSortOptions } from '../../redux/reducers/filters'
import { NumericInput } from '../common/NumericInput'
import { useClientApi } from '../../hooks/useClientApi'
import { useTranslation } from 'react-i18next'
import { newThemeObject } from '../../newTheme'
import DownloadIcon from '@mui/icons-material/Download';
import { RootState } from '../../redux/reducers/root'
import { useRoleApi } from '../../hooks/useRoleApi'
import { ClientRole } from '../../redux/reducers/clients'

const useStyles = makeStyles(theme => ({
  tableTopBar: {
    backgroundColor: newThemeObject.palette.orderTable.searchBar.bg,
    borderRadius: newThemeObject.roundness.basic,
    marginBottom: theme.spacing(2),
  },
  tableToolBar: {
    minHeight: 0,
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(2),
    margin: 0,
    flexDirection: 'column',
    paddingLeft: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  separator: {
    flexGrow: 1,
  },
  textField: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 200,
    },
    marginRight: theme.spacing(1),
  },
  icon: {
    color: newThemeObject.palette.main.sectext,
    backgroundColor: '#ffffff',
  },
  chips: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}))

interface ClientTableTopBarProps {
  setShowConfirmation: (param: boolean) => any
}

export const ClientTableTopBar = ({
  setShowConfirmation,
}: ClientTableTopBarProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { getClientsByParam, getClients } = useClientApi()
  const { t } = useTranslation()
  const { getClientRoles } = useClientApi()
  const roles: ClientRole[] = useSelector((state: RootState) => state.clients.clientRoles)
  const [instrumentFilter, setInstrumentFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState([] as ClientRole[])

  useEffect(() => {
    if ( !roles ) {
      getClientRoles()
    }
  }, [])
  
  const filters = useSelector(state => state.filters)
  const user = useSelector(state => state.auth.user)

  const onSortByChange = (event: SelectChangeEvent<string>) => {
    dispatch(sortClients(event.target.value))
  }

  const onNameSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientName(event.target.value))
  }

  const onRoleSearchFieldChange = (event: any, value: ClientRole[]) => {
    setRoleFilter(value);
    if (value.length === 0 && instrumentFilter==='' ) { 
      getClients()
    } else {
      getClientsByParam(instrumentFilter, getRoleIds(value));
    }
  }

  const getRoleIds = (value: ClientRole[]) => {
    let tmp = '';
    if ( value && value.length>0) {
      for ( let i=0; i<value.length; i++ ) {
        tmp+=tmp!==''?','+value[i].roleId:value[i].roleId;
      }
    }
    return tmp;
  }

  const onInstrumentSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    setInstrumentFilter(event.target.value);
    if (event.target.value === '' && roleFilter.length===0 ) {
      getClients();
    } else {
      getClientsByParam(event.target.value, getRoleIds(roleFilter));
    }
  }

  const onAmountUpSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientAmountUp(event.target.value))
  }

  const onAmountDownSearchFieldChange = (event: React.ChangeEvent<{ value: string }>) => {
    dispatch(filterClientAmountDown(event.target.value))
  }

  const showExportButton = () => {
    if ( user && user.roles ) {
      for ( let i=0; i<user.roles.length; i++) {
        if ( user.roles[i]==='CEO' 
          ||  user.roles[i]==='PORTF MANAGER' 
          || user.roles[i]==='ASSISTANT' ) {
          return true;
        }
      }
    } else {
      return false;
    }
  }

  useEffect(() => {
    dispatch(filterClientName(''))
  }, [])

  return (
    <AppBar position="static" className={classes.tableTopBar} elevation={0}>
      <Toolbar variant="dense" className={classes.tableToolBar}>
        <TextField
          className={classes.textField}
          placeholder={t('CLIENT')}
          onChange={onNameSearchFieldChange}
          value={filters.searchedClientName}
          variant="standard"
        />

        <NumericInput
          className={classes.textField}
          placeholder={t('AMOUNT_FROM')}
          onChange={onAmountUpSearchFieldChange}
          value={filters.searchedAmountUp}
          variant="standard"
        />

        <NumericInput
          className={classes.textField}
          placeholder={t('AMOUNT_TO')}
          onChange={onAmountDownSearchFieldChange}
          value={filters.searchedAmountDown}
          variant="standard"
        />
        <TextField
          className={classes.textField}
          placeholder={t('INSTRUMENT')}
          variant="standard"
          onChange={onInstrumentSearchFieldChange}
          value={instrumentFilter}
        />
        <FormControl style={{width:'400px'}}>
          <Autocomplete
            multiple
            id="roles"
            filterSelectedOptions
            options={roles}
            value={roleFilter}
            getOptionLabel={(option: ClientRole) => option.name}
            getOptionDisabled={option => roleFilter && !!roleFilter.find(r => r.roleId === option.roleId)}
            onChange={(event: any, value: ClientRole[]) => {
              onRoleSearchFieldChange(event, value);                        
            }}
            renderTags={(value: ClientRole[], getTagProps) =>
              value.map((option: ClientRole, index: number) => (
                <Chip classes={{ root: classes.chips }} label={option.name} {...getTagProps({ index })} />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="standard"
                placeholder="Role"
              />
            )}
          />
        </FormControl>
        <Select value={filters.sortBy} onChange={onSortByChange} variant="standard">
          <MenuItem value={ClientSortOptions.Az}>{t('NAME_A_Z')}</MenuItem>
          <MenuItem value={ClientSortOptions.Za}>{t('NAME_Z_A')}</MenuItem>
          <MenuItem value={ClientSortOptions.AmountDown}>{t('INVESTED_AMOUNT_DESC')}</MenuItem>
          <MenuItem value={ClientSortOptions.AmountUp}>{t('INVESTED_AMOUNT_ASK')}</MenuItem>
        </Select>

        <span className={classes.separator} />

        { showExportButton() &&
          <ColoredIconButton
            className={classes.icon}
            onClick={() => {
              setShowConfirmation(true);
            }}
            size="small"
            icon={<DownloadIcon />}
          />
        }

        <ColoredIconButton
          icon={<AddIcon />}
          className={classes.icon}
          size="small"
          onClick={() => {
            dispatch(
              openDialog(CLIENT_INFORMATION_DIALOG_ID, {
                dialogTitle: t('NEW_CLIENT'),
                type: 'new',
              })
            )
          }}
        />
      </Toolbar>
    </AppBar>
  )
}
